/**
 * usePresenceHeartbeat — true binary presence signal.
 *
 * While the user is authenticated AND the tab is visible, sends
 * POST /api/presence/heartbeat every 45s. On tab hide / pagehide,
 * fires a best-effort sendBeacon to /api/presence/leave so the user
 * flips to offline immediately. The backend TTL (300s) is the safety net
 * if the beacon doesn't fire (common on iOS / browser force-kill).
 *
 * Session scope: one heartbeat identity per tab. The session_id is a
 * random uuid held in memory only — it resets on reload, which is the
 * correct semantic (new tab = new session = new presence row).
 */
import { useEffect, useRef } from "react";
import api, { API_BASE_URL } from "@/lib/api";

const HEARTBEAT_MS = 45_000;

function makeSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback: random base36 + timestamp (no crypto dependency)
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export default function usePresenceHeartbeat(userId) {
  const sessionIdRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    if (!sessionIdRef.current) sessionIdRef.current = makeSessionId();
    const sessionId = sessionIdRef.current;

    const ping = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      api.post("/presence/heartbeat", { session_id: sessionId }).catch(() => {}); // silent
    };

    const leaveBeacon = () => {
      try {
        if (typeof navigator === "undefined") return;
        // Build the absolute URL (sendBeacon needs origin-relative only when same-origin is guaranteed)
        const baseUrl = API_BASE_URL;
        const url = `${baseUrl}/api/presence/leave`;
        const payload = new Blob(
          [JSON.stringify({ session_id: sessionId })],
          { type: "application/json" },
        );
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, payload);
        } else {
          // Best-effort sync fallback — POST will likely be cancelled, but TTL covers us.
          api.post("/presence/leave", { session_id: sessionId }).catch(() => {});
        }
      } catch { /* best-effort */ }
    };

    // Kick off immediately so the user shows online on first paint.
    ping();
    intervalRef.current = setInterval(ping, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        ping();
      } else {
        // Tab hidden — signal leave (not reload-safe, but correct for bg tabs).
        leaveBeacon();
      }
    };
    const onPageHide = () => leaveBeacon();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      // Final leave on unmount (e.g. logout)
      leaveBeacon();
    };
  }, [userId]);
}
